/**
 * LE BFR — besoin en fonds de roulement, semaine par semaine (lot 99,
 * 18/09/2026).
 *
 * Cadrage : « Analyse financière : 1. le BFR » et « 8. les arbitrages »,
 * maquette `maquette-analyse-bfr-v5.html`. Ce fichier est pur : il reçoit des
 * pièces et des ordres déjà lus, et rend pour chaque semaine les valeurs ET
 * les pièces qui les font — la fenêtre de détail de l'écran les énumère, et la
 * somme de ce qu'elle énumère est la valeur du graphique (contrôle
 * `__verifier` de la maquette).
 *
 * LES MONTANTS SONT EN CENTIMES.
 *
 * LES RÈGLES, et d'où elles viennent :
 *
 *   - BFR brut = clients non encaissés + transports livrés non facturés
 *     − fournisseurs non payés − affrètements dont la facture n'est pas reçue
 *     − charges sociales dues − TVA due (doc 1). BFR net = brut − factures
 *     cédées au factor (maquette v5).
 *   - Une semaine passée se lit à son dimanche, la semaine en cours à
 *     aujourd'hui, une semaine à venir à son dimanche avec les DATES PRÉVUES
 *     de l'échéancier (doc 8).
 *   - TVA : collectée − déductible du mois, due jusqu'au jour de paiement du
 *     mois suivant (24 par défaut, doc 8).
 *   - Charges sociales : comptées en fin de mois (doc 8), payées le 15 du
 *     mois suivant — le montant est un RÉGLAGE, Stafa n'a pas de paie.
 *   - DSO / DPO : encours ÷ volume TTC des 91 derniers jours × 91 ; réel =
 *     médiane (règlement − date de pièce), dates incohérentes exclues (doc 8).
 *   - Marge : CA à la livraison, charges du mois étalées par jour ; par
 *     activité ou client, coûts directs seulement, structure non répartie ;
 *     par client, coût réel Stafa, non disponible avant `debutStafa` (doc 8).
 *
 * SUPPOSÉ, ET RÉGLABLE (non tranché au cadrage) :
 *   - une facture d'affrètement arrive `delai_facture_affretement` jours
 *     après la livraison, et se paie `delai_paiement_affretement` jours après ;
 *   - un transport livré non facturé est facturé en fin de mois de
 *     livraison, payé `delai_client` jours après + retard habituel ;
 *   - toute facture fournisseur (hors affrètement) est une charge du mois de
 *     sa date, étalée par jour.
 */

import {
  ajouterJours,
  ecartJours,
  lundiDe,
  mediane,
  semaineIso,
  type OrigineDate,
} from './echeancier.js';

export interface ReglagesBfr {
  readonly jour_tva: number;
  readonly charges_sociales: number;
  readonly jour_charges_sociales: number;
  readonly delai_facture_affretement: number;
  readonly delai_paiement_affretement: number;
  readonly delai_client: number;
}

export const REGLAGES_BFR_DEFAUT: ReglagesBfr = {
  jour_tva: 24,
  charges_sociales: 0,
  jour_charges_sociales: 15,
  delai_facture_affretement: 30,
  delai_paiement_affretement: 30,
  delai_client: 45,
};

/** Une pièce Pennylane (client ou fournisseur). */
export interface PieceBfr {
  readonly cle: string;
  readonly numero: string | null;
  readonly tiers: string;
  readonly tiers_nom: string;
  readonly activite: string | null;
  readonly date: string;
  readonly echeance: string | null;
  readonly ttc: number;
  /**
   * LE HORS-TAXE, OU NUL QUAND IL N'EST PAS LISIBLE (lot 113, 21/09/2026).
   *
   * Jusqu'ici le HT manquant était remplacé par le TTC, ce qui paraissait
   * prudent et ne l'était pas : la pièce entrait alors dans le CHIFFRE
   * D'AFFAIRES (côté client) ou dans les CHARGES (côté fournisseur) pour son
   * montant TTC, soit environ 20 % de trop, sans que rien ne le dise.
   *
   * ARBITRAGE DE L'EXPLOITATION (21/09/2026) : une pièce dont le hors-taxe
   * n'est pas lisible est ÉCARTÉE des trois calculs qui s'en servent — TVA,
   * ventes, charges — et ANNONCÉE. Un chiffre juste sur moins de pièces vaut
   * mieux qu'un chiffre faux sur toutes.
   *
   * ELLE N'EST PAS ÉCARTÉE DU RESTE : son TTC continue de peser sur le BFR et
   * sur l'échéancier, où le hors-taxe ne sert à rien.
   */
  readonly ht: number | null;
  /** Date du règlement complet, ou nul. */
  readonly regle_le: string | null;
  /**
   * Faux quand la pièce est payée mais que sa date de paiement n'a pas
   * encore été lue : `regle_le` est alors l'échéance, et la pièce n'entre pas
   * dans le délai réel.
   */
  readonly regle_connu: boolean;
  /**
   * Pour une pièce ouverte aujourd'hui : ses parts à venir (échéancier),
   * chacune à sa date. Vide pour une pièce réglée.
   */
  readonly parts: readonly {
    readonly date: string;
    readonly montant: number;
    /**
     * D'où vient la date (lot 100) : la trésorerie n'applique le pire
     * retard qu'aux dates CALCULÉES. Facultatif : le BFR ne s'en sert pas.
     */
    readonly origine?: OrigineDate;
    readonly a_revoir?: boolean;
  }[];
  /** Reste dû aujourd'hui (≤ ttc). */
  readonly reste: number;
  readonly cedee_le: string | null;
}

/** Un ordre de transport Stafa. */
export interface OrdreBfr {
  readonly id: string;
  readonly numero: string;
  readonly tiers: string;
  readonly tiers_nom: string;
  readonly activite: string | null;
  /** Dernier jour d'étape — livré ou prévu. */
  readonly livre_le: string;
  readonly annule: boolean;
  readonly vente_ht: number;
  readonly vente_ttc: number;
  /** Date d'émission de sa facture, ou nul. */
  readonly facture_le: string | null;
  /** Coût réel figé (temps + km + péage + achat), ou nul. */
  readonly cout_reel: number | null;
  /** Prix d'achat de l'affrètement actif, ou nul. */
  readonly achat_ht: number | null;
  /** Retard habituel du client, pour la projection. */
  readonly retard_client: number;
}

/** Une ligne prévue (contrat, estimation) déjà ramenée à la vue. */
export interface LignePrevueBfr {
  readonly cle: string;
  readonly cote: 'client' | 'fournisseur';
  readonly tiers: string;
  readonly tiers_nom: string;
  readonly libelle: string;
  readonly date: string;
  readonly reglement: string;
  readonly ttc: number;
  readonly ht: number;
  /** Part par activité (`''` : structure), de 0 à 1. */
  readonly parts: Readonly<Record<string, number>>;
  /** Rythme mensuel : charge étalée sur le mois. */
  readonly mensuelle: boolean;
}

export interface EntreesBfr {
  readonly aujourdhui: string;
  readonly debutStafa: string;
  readonly reglages: ReglagesBfr;
  readonly clients: readonly PieceBfr[];
  readonly fournisseurs: readonly PieceBfr[];
  readonly ordres: readonly OrdreBfr[];
  readonly lignes: readonly LignePrevueBfr[];
  /** Tiers clients dont un contrat remplace les transports planifiés. */
  readonly tiersRemplaces: ReadonlySet<string>;
}

export interface Filtre {
  /** `tout`, un identifiant d'activité, ou `sans`. */
  readonly activite: string;
  /** `tout` ou une clé de tiers client. */
  readonly client: string;
}

export interface Semaine {
  readonly numero: number;
  readonly lundi: string;
  readonly dimanche: string;
  readonly arrete: string;
  readonly courante: boolean;
  readonly future: boolean;
}

export type Fenetre = 'centre' | 'passe' | 'avenir';

export function semainesDe(aujourdhui: string, fenetre: Fenetre): Semaine[] {
  const [de, a] =
    fenetre === 'centre' ? [-6, 6] : fenetre === 'passe' ? [-12, 0] : [0, 12];
  const lundi0 = lundiDe(aujourdhui);
  const res: Semaine[] = [];
  for (let k = de; k <= a; k += 1) {
    const lundi = ajouterJours(lundi0, 7 * k);
    const dimanche = ajouterJours(lundi, 6);
    const courante = k === 0;
    res.push({
      numero: semaineIso(lundi),
      lundi,
      dimanche,
      arrete: courante ? aujourdhui : dimanche,
      courante,
      future: k > 0,
    });
  }
  return res;
}

export function finDeMois(iso: string): string {
  const a = Number(iso.slice(0, 4));
  const m = Number(iso.slice(5, 7));
  return new Date(Date.UTC(a, m, 0)).toISOString().slice(0, 10);
}

export function moisSuivant(iso: string, jour: number): string {
  const fin = finDeMois(iso);
  const suivant = ajouterJours(fin, 1);
  const dernier = Number(finDeMois(suivant).slice(8, 10));
  return `${suivant.slice(0, 8)}${String(Math.min(jour, dernier)).padStart(2, '0')}`;
}

const somme = <T>(l: readonly T[], f: (x: T) => number) =>
  l.reduce((s, x) => s + f(x), 0);

// ---------------------------------------------------------------------------
// Le filtre
// ---------------------------------------------------------------------------

export const vueEnsemble = (f: Filtre) => f.activite === 'tout' && f.client === 'tout';

function garderActivite(f: Filtre, activite: string | null): boolean {
  if (f.activite === 'tout') return true;
  if (f.activite === 'sans') return activite === null;
  return activite === f.activite;
}

function garderClient(
  f: Filtre,
  p: { tiers: string; activite: string | null },
): boolean {
  return (f.client === 'tout' || p.tiers === f.client) && garderActivite(f, p.activite);
}

/**
 * UNE PIÈCE FOURNISSEUR se rattache à une vue d'activité par le réglage du
 * fournisseur, et à AUCUNE vue client : par client, seuls les affrètements de
 * ses ordres comptent (doc 8).
 */
function garderFournisseur(f: Filtre, p: PieceBfr): boolean {
  if (f.client !== 'tout') return false;
  return garderActivite(f, p.activite);
}

/** La part d'une ligne prévue qui revient à la vue. */
function partVue(f: Filtre, parts: Readonly<Record<string, number>>): number {
  if (f.activite === 'tout') return 1;
  return parts[f.activite === 'sans' ? '' : f.activite] ?? 0;
}

function garderOrdre(f: Filtre, o: OrdreBfr): boolean {
  return !o.annule && garderClient(f, o);
}

// ---------------------------------------------------------------------------
// Ouvert à une date
// ---------------------------------------------------------------------------

/**
 * LE RESTE D'UNE PIÈCE À UNE DATE. Passé : émise et pas encore réglée — tout
 * le TTC (un règlement partiel ancien ne se date pas). Futur : la somme de ses
 * parts prévues APRÈS cette date.
 */
export function resteA(p: PieceBfr, d: string, aujourdhui: string): number {
  if (p.date > d) return 0;
  if (d <= aujourdhui) {
    if (p.regle_le !== null && p.regle_le <= d) return 0;
    if (d === aujourdhui) return p.reste;
    return p.ttc;
  }
  /**
   * UNE PIÈCE OUVERTE SANS AUCUNE PART reste due en entier (lot 110,
   * 21/09/2026). Depuis que les pièces échues ne reçoivent plus de date
   * inventée (`partsDe`, echeancier.ts), une pièce peut n'avoir aucune part et
   * rester pourtant impayée. Sans cette ligne, la somme ci-dessous vaudrait
   * zéro et la pièce sortirait du BFR dès la première semaine à venir — le
   * défaut même que le lot 108 avait rendu visible.
   *
   * Une pièce RÉGLÉE a elle aussi des parts vides, mais son reste dû est nul :
   * c'est ce qui les distingue, et c'est pourquoi le test porte sur le reste.
   */
  if (p.parts.length === 0) return p.reste > 0 ? p.reste : 0;

  return somme(
    p.parts.filter((x) => x.date > d),
    (x) => x.montant,
  );
}

/** Date projetée de facture et de règlement d'un ordre non facturé aujourd'hui. */
export function projectionOrdre(
  o: OrdreBfr,
  e: EntreesBfr,
): { facture: string; reglement: string } {
  const fin = finDeMois(o.livre_le);
  const facture = fin < e.aujourdhui ? e.aujourdhui : fin;
  return {
    facture,
    reglement: ajouterJours(
      facture,
      e.reglages.delai_client + Math.max(0, o.retard_client),
    ),
  };
}

function ordreProjete(o: OrdreBfr, e: EntreesBfr): boolean {
  return o.facture_le === null && !e.tiersRemplaces.has(o.tiers);
}

// ---------------------------------------------------------------------------
// Le BFR
// ---------------------------------------------------------------------------

export interface ElementBfr {
  readonly cle: string;
  readonly libelle: string;
  readonly tiers: string;
  readonly date: string;
  readonly montant: number;
  readonly note: string;
}

export interface BlocBfr {
  readonly cle:
    | 'clients'
    | 'non_factures'
    | 'fournisseurs'
    | 'affretements'
    | 'social'
    | 'tva'
    | 'cedees';
  readonly titre: string;
  readonly signe: 1 | -1;
  readonly montant: number;
  readonly elements: readonly ElementBfr[];
}

export interface BfrSemaine {
  readonly semaine: Semaine;
  readonly blocs: readonly BlocBfr[];
  readonly brut: number;
  readonly net: number;
}

function element(
  cle: string,
  libelle: string,
  tiers: string,
  date: string,
  montant: number,
  note: string,
): ElementBfr {
  return { cle, libelle, tiers, date, montant, note };
}

function bloc(
  cle: BlocBfr['cle'],
  titre: string,
  signe: 1 | -1,
  elements: ElementBfr[],
): BlocBfr {
  return { cle, titre, signe, montant: somme(elements, (x) => x.montant), elements };
}

export function bfrSemaine(e: EntreesBfr, f: Filtre, s: Semaine): BfrSemaine {
  const d = s.arrete;
  const auj = e.aujourdhui;
  const futur = d > auj;
  const ens = vueEnsemble(f);

  // Clients
  const clients: ElementBfr[] = [];
  for (const p of e.clients) {
    if (!garderClient(f, p)) continue;
    const r = resteA(p, d, auj);
    if (r > 0) {
      clients.push(
        element(
          p.cle,
          p.numero ?? p.cle,
          p.tiers_nom,
          p.date,
          r,
          futur ? 'reste prévu' : 'non encaissée',
        ),
      );
    }
  }
  const nonFactures: ElementBfr[] = [];
  const fournisseurs: ElementBfr[] = [];
  const affretements: ElementBfr[] = [];
  for (const o of e.ordres) {
    if (!garderOrdre(f, o) || o.livre_le > d) continue;
    // UN ORDRE PLANIFIÉ D'UN CLIENT SOUS CONTRAT n'est pas projeté : les
    // lignes du contrat le remplacent (doc 3). Un ordre déjà livré reste.
    const remplace = o.livre_le > auj && e.tiersRemplaces.has(o.tiers);
    // Non facturé à la date
    if (!remplace && (o.facture_le === null || o.facture_le > d)) {
      if (!futur) {
        nonFactures.push(
          element(
            `ot:${o.id}`,
            o.numero,
            o.tiers_nom,
            o.livre_le,
            o.vente_ttc,
            'livré, non facturé',
          ),
        );
      } else if (o.facture_le === null) {
        const pr = projectionOrdre(o, e);
        if (pr.facture > d) {
          nonFactures.push(
            element(
              `ot:${o.id}`,
              o.numero,
              o.tiers_nom,
              o.livre_le,
              o.vente_ttc,
              `facture prévue le ${pr.facture}`,
            ),
          );
        } else if (pr.reglement > d) {
          clients.push(
            element(
              `ot:${o.id}`,
              `${o.numero} (à facturer)`,
              o.tiers_nom,
              pr.facture,
              o.vente_ttc,
              `règlement prévu le ${pr.reglement}`,
            ),
          );
        }
      }
    }
    // Affrètement : facture non reçue, puis dette projetée.
    if (o.achat_ht !== null && o.achat_ht > 0) {
      const ttc = Math.round(o.achat_ht * 1.2);
      const recue = ajouterJours(o.livre_le, e.reglages.delai_facture_affretement);
      const payee = ajouterJours(recue, e.reglages.delai_paiement_affretement);
      if (d < recue) {
        affretements.push(
          element(
            `aff:${o.id}`,
            o.numero,
            o.tiers_nom,
            o.livre_le,
            ttc,
            `facture attendue vers le ${recue}`,
          ),
        );
      } else if (futur && recue > auj && d < payee) {
        // La facture n'était pas encore reçue aujourd'hui : elle n'est pas
        // dans Pennylane, on la projette.
        fournisseurs.push(
          element(
            `aff:${o.id}`,
            `Affrètement ${o.numero}`,
            o.tiers_nom,
            recue,
            ttc,
            `paiement prévu le ${payee}`,
          ),
        );
      }
    }
  }
  for (const p of e.fournisseurs) {
    if (!garderFournisseur(f, p)) continue;
    const r = resteA(p, d, auj);
    if (r > 0) {
      fournisseurs.push(
        element(
          p.cle,
          p.numero ?? p.cle,
          p.tiers_nom,
          p.date,
          r,
          futur ? 'reste prévu' : 'non payée',
        ),
      );
    }
  }
  // Lignes prévues (contrats, estimations) : facturées, pas encore réglées.
  if (futur) {
    for (const l of e.lignes) {
      if (l.date > d || l.reglement <= d || l.date < auj) continue;
      if (f.client !== 'tout' && (l.cote === 'fournisseur' || l.tiers !== f.client))
        continue;
      const m = Math.round(l.ttc * partVue(f, l.parts));
      if (m === 0) continue;
      const el = element(
        `prev:${l.cle}`,
        l.libelle,
        l.tiers_nom,
        l.date,
        m,
        `prévision, règlement le ${l.reglement}`,
      );
      if (l.cote === 'client') clients.push(el);
      else fournisseurs.push(el);
    }
  }

  // Charges sociales et TVA : vue d'ensemble seulement.
  const social: ElementBfr[] = [];
  const tva: ElementBfr[] = [];
  if (ens) {
    const M = e.reglages.charges_sociales;
    if (M > 0) {
      const fin = finDeMois(d);
      const mPrec = finDeMois(ajouterJours(`${d.slice(0, 8)}01`, -1));
      if (Number(d.slice(8, 10)) < e.reglages.jour_charges_sociales) {
        social.push(
          element(
            `soc:${mPrec}`,
            `Charges sociales de ${mPrec.slice(0, 7)}`,
            'Réglage',
            mPrec,
            M,
            `payées le ${moisSuivant(mPrec, e.reglages.jour_charges_sociales)}`,
          ),
        );
      }
      if (d === fin) {
        social.push(
          element(
            `soc:${fin}`,
            `Charges sociales de ${fin.slice(0, 7)}`,
            'Réglage',
            fin,
            M,
            'comptées en fin de mois',
          ),
        );
      }
    }
    for (const mois of [
      ajouterJours(`${d.slice(0, 8)}01`, -1).slice(0, 7),
      d.slice(0, 7),
    ]) {
      const debut = `${mois}-01`;
      const paiement = moisSuivant(debut, e.reglages.jour_tva);
      if (debut > d || paiement <= d) continue;
      const dans = (x: string) => x.startsWith(mois) && x <= d;
      // SANS HORS-TAXE LISIBLE, PAS DE TVA DÉDUITE (lot 113). Le HT manquant
      // valait le TTC, donc `ttc - ht` valait zéro : le chiffre ne bouge pas,
      // mais la pièce est désormais ÉCARTÉE et comptée comme telle, au lieu
      // d'être noyée dans le total avec une TVA nulle inventée.
      let coll = somme(
        e.clients.filter((p) => dans(p.date) && p.ht !== null),
        (p) => p.ttc - (p.ht as number),
      );
      let ded = somme(
        e.fournisseurs.filter((p) => dans(p.date) && p.ht !== null),
        (p) => p.ttc - (p.ht as number),
      );
      if (futur) {
        for (const l of e.lignes) {
          if (!dans(l.date) || l.date < auj) continue;
          if (l.cote === 'client') coll += l.ttc - l.ht;
          else ded += l.ttc - l.ht;
        }
        for (const o of e.ordres) {
          if (o.annule || !ordreProjete(o, e)) continue;
          const pr = projectionOrdre(o, e);
          if (dans(pr.facture) && pr.facture > auj) coll += o.vente_ttc - o.vente_ht;
        }
      }
      const net = coll - ded;
      if (net !== 0) {
        tva.push(
          element(
            `tva:${mois}`,
            `TVA de ${mois}`,
            'Trésor public',
            debut,
            net,
            `collectée ${coll} − déductible ${ded}, payée le ${paiement}`,
          ),
        );
      }
    }
  }

  const cedees: ElementBfr[] = clients
    .map((x) => ({ x, p: e.clients.find((p) => p.cle === x.cle) }))
    .filter(({ p }) => p !== undefined && p.cedee_le !== null && p.cedee_le <= d)
    .map(({ x }) => ({ ...x, note: 'cédée au factor' }));

  const blocs = [
    bloc('clients', 'Factures clients non encaissées', 1, clients),
    bloc('non_factures', 'Transports livrés, pas encore facturés', 1, nonFactures),
    bloc('fournisseurs', 'Fournisseurs non payés', -1, fournisseurs),
    bloc(
      'affretements',
      'Affrètements dont la facture n’est pas reçue',
      -1,
      affretements,
    ),
    bloc('social', 'Charges sociales dues', -1, social),
    bloc('tva', 'TVA due', -1, tva),
    bloc('cedees', 'Factures cédées au factor', -1, cedees),
  ];
  const brut = somme(
    blocs.filter((b) => b.cle !== 'cedees'),
    (b) => b.signe * b.montant,
  );
  const cede = blocs.find((b) => b.cle === 'cedees')?.montant ?? 0;
  return { semaine: s, blocs, brut, net: brut - cede };
}

// ---------------------------------------------------------------------------
// Encaissements et paiements
// ---------------------------------------------------------------------------

export interface FluxSemaine {
  readonly semaine: Semaine;
  readonly attendus: readonly ElementBfr[];
  readonly realises: readonly ElementBfr[];
  readonly attendu: number;
  readonly realise: number | null;
}

export function fluxSemaine(
  e: EntreesBfr,
  f: Filtre,
  s: Semaine,
  cote: 'client' | 'fournisseur',
): FluxSemaine {
  const dans = (x: string | null) => x !== null && x >= s.lundi && x <= s.dimanche;
  const pieces =
    cote === 'client'
      ? e.clients.filter((p) => garderClient(f, p))
      : e.fournisseurs.filter((p) => garderFournisseur(f, p));
  const attendus: ElementBfr[] = [];
  const realises: ElementBfr[] = [];
  if (s.future) {
    for (const p of pieces) {
      for (const x of p.parts) {
        if (dans(x.date))
          attendus.push(
            element(
              p.cle,
              p.numero ?? p.cle,
              p.tiers_nom,
              x.date,
              x.montant,
              'date prévue',
            ),
          );
      }
    }
    for (const l of e.lignes) {
      if (l.cote !== cote || !dans(l.reglement)) continue;
      if (f.client !== 'tout' && (cote === 'fournisseur' || l.tiers !== f.client))
        continue;
      const m = Math.round(l.ttc * partVue(f, l.parts));
      if (m > 0)
        attendus.push(
          element(`prev:${l.cle}`, l.libelle, l.tiers_nom, l.reglement, m, 'prévision'),
        );
    }
  } else {
    for (const p of pieces) {
      if (dans(p.echeance))
        attendus.push(
          element(
            p.cle,
            p.numero ?? p.cle,
            p.tiers_nom,
            p.echeance ?? p.date,
            p.ttc,
            'échéance',
          ),
        );
      if (dans(p.regle_le) && (p.regle_le ?? '') <= e.aujourdhui) {
        realises.push(
          element(
            p.cle,
            p.numero ?? p.cle,
            p.tiers_nom,
            p.regle_le ?? '',
            p.ttc,
            'réglée',
          ),
        );
      }
    }
  }
  return {
    semaine: s,
    attendus,
    realises,
    attendu: somme(attendus, (x) => x.montant),
    realise: s.future ? null : somme(realises, (x) => x.montant),
  };
}

// ---------------------------------------------------------------------------
// DSO et DPO
// ---------------------------------------------------------------------------

export interface DelaiSemaine {
  readonly semaine: Semaine;
  readonly encours: number;
  readonly volume: number;
  readonly comptable: number | null;
  readonly reel: number | null;
  readonly regles: readonly ElementBfr[];
  readonly exclues: readonly ElementBfr[];
}

export function delaiSemaine(
  e: EntreesBfr,
  f: Filtre,
  s: Semaine,
  cote: 'client' | 'fournisseur',
  bfr: BfrSemaine,
): DelaiSemaine {
  const d = s.arrete;
  const debut = ajouterJours(d, -90);
  const encours =
    bfr.blocs.find((b) => b.cle === (cote === 'client' ? 'clients' : 'fournisseurs'))
      ?.montant ?? 0;
  const pieces =
    cote === 'client'
      ? e.clients.filter((p) => garderClient(f, p))
      : e.fournisseurs.filter((p) => garderFournisseur(f, p));
  let volume = somme(
    pieces.filter((p) => p.date >= debut && p.date <= d),
    (p) => p.ttc,
  );
  if (s.future) {
    volume += somme(
      e.lignes.filter(
        (l) =>
          l.cote === cote &&
          l.date >= debut &&
          l.date <= d &&
          l.date >= e.aujourdhui &&
          (f.client === 'tout' || (cote === 'client' && l.tiers === f.client)),
      ),
      (l) => l.ttc,
    );
  }
  const regles: ElementBfr[] = [];
  const exclues: ElementBfr[] = [];
  const delais: number[] = [];
  if (!s.future) {
    for (const p of pieces) {
      if (p.regle_le === null || !p.regle_connu || p.regle_le < debut || p.regle_le > d)
        continue;
      const el = element(
        p.cle,
        p.numero ?? p.cle,
        p.tiers_nom,
        p.regle_le,
        p.ttc,
        `${String(ecartJours(p.date, p.regle_le))} j`,
      );
      if (p.regle_le < p.date) {
        exclues.push({ ...el, note: 'réglée avant d’être émise' });
        continue;
      }
      regles.push(el);
      delais.push(ecartJours(p.date, p.regle_le));
    }
  }
  const m = mediane(delais);
  return {
    semaine: s,
    encours,
    volume,
    comptable: volume > 0 ? Math.round((encours / volume) * 91) : null,
    reel: m === null ? null : Math.round(m),
    regles,
    exclues,
  };
}

// ---------------------------------------------------------------------------
// La marge
// ---------------------------------------------------------------------------

export interface MargeSemaine {
  readonly semaine: Semaine;
  readonly ca: number;
  readonly exploitation: number | null;
  readonly structure: number | null;
  readonly marge: number | null;
  readonly ventes: readonly ElementBfr[];
  readonly charges: readonly ElementBfr[];
  /** Par client, avant `debutStafa` : le coût n'existe pas. */
  readonly non_disponible: boolean;
}

function joursDansSemaine(date: string, s: Semaine): { jours: number; mois: number } {
  const debut = `${date.slice(0, 8)}01`;
  const fin = finDeMois(date);
  const a = debut > s.lundi ? debut : s.lundi;
  const z = fin < s.dimanche ? fin : s.dimanche;
  return { jours: z >= a ? ecartJours(a, z) + 1 : 0, mois: Number(fin.slice(8, 10)) };
}

export function margeSemaine(e: EntreesBfr, f: Filtre, s: Semaine): MargeSemaine {
  const dans = (x: string) => x >= s.lundi && x <= s.dimanche;
  const ens = vueEnsemble(f);
  const parClient = f.client !== 'tout';
  const ventes: ElementBfr[] = [];
  const charges: ElementBfr[] = [];
  let exploitation = 0;
  let structure = 0;
  let nonDisponible = false;

  // Ventes : Pennylane avant Stafa, ordres Stafa ensuite, prévisions à venir.
  for (const p of e.clients) {
    if (p.date >= e.debutStafa || !dans(p.date) || !garderClient(f, p)) continue;
    // SANS HORS-TAXE LISIBLE, PAS DE VENTE COMPTÉE (lot 113). C'est ICI que
    // le remplacement du HT par le TTC coûtait le plus cher : le chiffre
    // d'affaires de la semaine était gonflé d'environ 20 % sur cette pièce,
    // en silence. Elle est écartée et annoncée.
    if (p.ht === null) continue;
    ventes.push(
      element(p.cle, p.numero ?? p.cle, p.tiers_nom, p.date, p.ht, 'facture'),
    );
  }
  for (const o of e.ordres) {
    if (!garderOrdre(f, o) || o.livre_le < e.debutStafa || !dans(o.livre_le)) continue;
    if (o.livre_le > e.aujourdhui && e.tiersRemplaces.has(o.tiers)) continue;
    ventes.push(
      element(`ot:${o.id}`, o.numero, o.tiers_nom, o.livre_le, o.vente_ht, 'livraison'),
    );
    if (parClient) {
      if (o.cout_reel === null) continue;
      exploitation += o.cout_reel;
      charges.push(
        element(
          `cout:${o.id}`,
          `Coût réel ${o.numero}`,
          o.tiers_nom,
          o.livre_le,
          o.cout_reel,
          'coût de revient Stafa',
        ),
      );
    } else if (o.achat_ht !== null && o.livre_le > e.aujourdhui) {
      // Affrètement à venir : pas encore dans Pennylane.
      exploitation += o.achat_ht;
      charges.push(
        element(
          `aff:${o.id}`,
          `Affrètement ${o.numero}`,
          o.tiers_nom,
          o.livre_le,
          o.achat_ht,
          'achat prévu',
        ),
      );
    }
  }
  if (parClient && s.dimanche < e.debutStafa) nonDisponible = true;

  for (const l of e.lignes) {
    if (l.date < e.aujourdhui) continue;
    if (l.cote === 'client') {
      if (!dans(l.date) || (parClient && l.tiers !== f.client)) continue;
      const m = Math.round(l.ht * partVue(f, l.parts));
      if (m > 0)
        ventes.push(
          element(`prev:${l.cle}`, l.libelle, l.tiers_nom, l.date, m, 'prévision'),
        );
    } else if (!parClient) {
      const js = l.mensuelle
        ? joursDansSemaine(l.date, s)
        : { jours: dans(l.date) ? 1 : 0, mois: 1 };
      if (js.jours === 0) continue;
      for (const [act, part] of Object.entries(l.parts)) {
        if (act === '' ? !ens : !(f.activite === 'tout' || f.activite === act))
          continue;
        const m = Math.round((l.ht * part * js.jours) / js.mois);
        if (m === 0) continue;
        if (act === '') structure += m;
        else exploitation += m;
        charges.push(
          element(
            `prev:${l.cle}:${act}`,
            l.libelle,
            l.tiers_nom,
            l.date,
            m,
            l.mensuelle ? `${String(js.jours)} j / ${String(js.mois)}` : 'prévision',
          ),
        );
      }
    }
  }

  if (!parClient) {
    for (const p of e.fournisseurs) {
      if (p.activite === null ? !ens : !garderActivite(f, p.activite)) continue;
      // SANS HORS-TAXE LISIBLE, PAS DE CHARGE COMPTÉE (lot 113). Symétrique
      // des ventes : la charge était gonflée d'environ 20 %. Les deux erreurs
      // jouaient en sens inverse sur la marge, ce qui les rendait invisibles
      // sur le résultat alors que chaque ligne était fausse.
      if (p.ht === null) continue;
      const js = joursDansSemaine(p.date, s);
      if (js.jours === 0) continue;
      const m = Math.round((p.ht * js.jours) / js.mois);
      if (m === 0) continue;
      if (p.activite === null) structure += m;
      else exploitation += m;
      charges.push(
        element(
          p.cle,
          p.numero ?? p.cle,
          p.tiers_nom,
          p.date,
          m,
          `${String(js.jours)} j / ${String(js.mois)}`,
        ),
      );
    }
  }

  const ca = somme(ventes, (x) => x.montant);
  return {
    semaine: s,
    ca,
    exploitation: nonDisponible ? null : exploitation,
    structure: ens ? structure : null,
    marge: nonDisponible ? null : ca - exploitation - (ens ? structure : 0),
    ventes,
    charges,
    non_disponible: nonDisponible,
  };
}

/**
 * LE CONTRÔLE DE LA MAQUETTE, en code : chaque valeur est la somme de ce que
 * sa fenêtre de détail énumère. Rend la liste des écarts (vide si tout va).
 */
export function verifier(
  e: EntreesBfr,
  f: Filtre,
  semaines: readonly Semaine[],
): string[] {
  const ecarts: string[] = [];
  for (const s of semaines) {
    const b = bfrSemaine(e, f, s);
    for (const x of b.blocs) {
      if (x.montant !== somme(x.elements, (y) => y.montant))
        ecarts.push(`S${String(s.numero)} ${x.cle}`);
    }
    const brut = somme(
      b.blocs.filter((x) => x.cle !== 'cedees'),
      (x) => x.signe * x.montant,
    );
    if (brut !== b.brut) ecarts.push(`S${String(s.numero)} brut`);
    for (const c of ['client', 'fournisseur'] as const) {
      const fl = fluxSemaine(e, f, s, c);
      if (fl.attendu !== somme(fl.attendus, (y) => y.montant))
        ecarts.push(`S${String(s.numero)} ${c} attendu`);
    }
    const m = margeSemaine(e, f, s);
    if (m.ca !== somme(m.ventes, (y) => y.montant))
      ecarts.push(`S${String(s.numero)} ca`);
    if (
      m.exploitation !== null &&
      m.structure !== null &&
      m.exploitation + m.structure !== somme(m.charges, (y) => y.montant)
    ) {
      ecarts.push(`S${String(s.numero)} charges`);
    }
  }
  return ecarts;
}
